import { ReactNode } from "react";
import { SvgIcon, SvgIconProps } from "@mui/material";

/**
 * Line icons for "Build with AI". The design draws them as stroked 24px glyphs
 * (the Lucide set), like the other Web Forms icons, rather than Material ones.
 *
 * They inherit `currentColor`; size comes from the caller's `sx`.
 */

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const glyph = (paths: ReactNode, name: string) => {
  const Glyph = (props: SvgIconProps) => (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      {paths}
    </SvgIcon>
  );
  Glyph.displayName = name;
  return Glyph;
};

export const SparkleGlyph = glyph(
  <path
    {...stroke}
    d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
  />,
  "SparkleGlyph",
);

export const AiPromptGlyph = glyph(
  <>
    <path
      {...stroke}
      d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
    />
    <line {...stroke} x1="12" y1="8" x2="12" y2="14" />
    <line {...stroke} x1="9" y1="11" x2="15" y2="11" />
  </>,
  "AiPromptGlyph",
);

export const ChevronLeftGlyph = glyph(<path {...stroke} d="m15 18-6-6 6-6" />, "ChevronLeftGlyph");

export const ChevronDownGlyph = glyph(<path {...stroke} d="m6 9 6 6 6-6" />, "ChevronDownGlyph");

export const HistoryGlyph = glyph(
  <>
    <path {...stroke} d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path {...stroke} d="M3 3v5h5" />
    <path {...stroke} d="M12 7v5l4 2" />
  </>,
  "HistoryGlyph",
);

export const RestoreGlyph = glyph(
  <>
    <path {...stroke} d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path {...stroke} d="M3 3v5h5" />
  </>,
  "RestoreGlyph",
);

export const GlobeGlyph = glyph(
  <>
    <circle {...stroke} cx="12" cy="12" r="10" />
    <path {...stroke} d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path {...stroke} d="M2 12h20" />
  </>,
  "GlobeGlyph",
);

export const MoreGlyph = glyph(
  <>
    <circle {...stroke} cx="12" cy="12" r="1" />
    <circle {...stroke} cx="19" cy="12" r="1" />
    <circle {...stroke} cx="5" cy="12" r="1" />
  </>,
  "MoreGlyph",
);

export const InfoGlyph = glyph(
  <>
    <circle {...stroke} cx="12" cy="12" r="10" />
    <path {...stroke} d="M12 16v-4" />
    <path {...stroke} d="M12 8h.01" />
  </>,
  "InfoGlyph",
);

export const CheckGlyph = glyph(<path {...stroke} d="M20 6 9 17l-5-5" />, "CheckGlyph");

export const EyeGlyph = glyph(
  <>
    <path
      {...stroke}
      d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"
    />
    <circle {...stroke} cx="12" cy="12" r="3" />
  </>,
  "EyeGlyph",
);

export const UserGlyph = glyph(
  <>
    <path {...stroke} d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle {...stroke} cx="12" cy="7" r="4" />
  </>,
  "UserGlyph",
);

export const ArrowUpGlyph = glyph(
  <>
    <path {...stroke} d="m5 12 7-7 7 7" />
    <path {...stroke} d="M12 19V5" />
  </>,
  "ArrowUpGlyph",
);

export const MonitorGlyph = glyph(
  <>
    <rect {...stroke} x="2" y="3" width="20" height="14" rx="2" />
    <path {...stroke} d="M8 21h8" />
    <path {...stroke} d="M12 17v4" />
  </>,
  "MonitorGlyph",
);

export const PhoneGlyph = glyph(
  <>
    <rect {...stroke} x="5" y="2" width="14" height="20" rx="2" />
    <path {...stroke} d="M12 18h.01" />
  </>,
  "PhoneGlyph",
);

export const CloseGlyph = glyph(
  <>
    <path {...stroke} d="M18 6 6 18" />
    <path {...stroke} d="m6 6 12 12" />
  </>,
  "CloseGlyph",
);

export const AlertGlyph = glyph(
  <>
    <path {...stroke} d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
    <path {...stroke} d="M12 9v4" />
    <path {...stroke} d="M12 17h.01" />
  </>,
  "AlertGlyph",
);

export const TextGlyph = glyph(
  <>
    <path {...stroke} d="M4 7V4h16v3" />
    <path {...stroke} d="M9 20h6" />
    <path {...stroke} d="M12 4v16" />
  </>,
  "TextGlyph",
);

export const RefreshGlyph = glyph(
  <>
    <path {...stroke} d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path {...stroke} d="M21 3v5h-5" />
    <path {...stroke} d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path {...stroke} d="M8 16H3v5" />
  </>,
  "RefreshGlyph",
);

export const FileGlyph = glyph(
  <>
    <path {...stroke} d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path {...stroke} d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path {...stroke} d="M10 9H8" />
    <path {...stroke} d="M16 13H8" />
    <path {...stroke} d="M16 17H8" />
  </>,
  "FileGlyph",
);

export const LayoutGlyph = glyph(
  <>
    <rect {...stroke} x="3" y="3" width="18" height="7" rx="1" />
    <rect {...stroke} x="3" y="14" width="9" height="7" rx="1" />
    <rect {...stroke} x="16" y="14" width="5" height="7" rx="1" />
  </>,
  "LayoutGlyph",
);

export const PencilGlyph = glyph(
  <>
    <path
      {...stroke}
      d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
    />
    <path {...stroke} d="m15 5 4 4" />
  </>,
  "PencilGlyph",
);
